import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BarChart3, 
  TrendingUp, 
  ShoppingCart, 
  Package, 
  DollarSign,
  Calendar,
  Download,
  RefreshCw,
  Store,
  MessageCircle,
  Globe
} from 'lucide-react';
import { getSalePaymentLabel } from '@/lib/payments';

const API_URL = BACKEND_URL || '';

// Função para fazer requisições autenticadas
const fetchAPI = async (url) => {
  const token = localStorage.getItem('token');
  const response = await fetch(url, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('Erro na requisição');
  return response.json();
};

const ReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [modulesSummary, setModulesSummary] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [selectedModule, setSelectedModule] = useState('all');
  const [moduleProducts, setModuleProducts] = useState([]);
  const [moduleSales, setModuleSales] = useState({ sales: [], total: 0 });
  const [dateRange, setDateRange] = useState('30');
  const [enabledModules, setEnabledModules] = useState({ pdv: true, whatsapp: false, online: false });

  const getDateRange = useCallback(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - parseInt(dateRange));
    return {
      start_date: start.toISOString(),
      end_date: end.toISOString()
    };
  }, [dateRange]);

  const fetchModulesSummary = useCallback(async () => {
    try {
      const { start_date, end_date } = getDateRange();
      const response = await fetchAPI(`${API_URL}/api/reports/modules/summary?start_date=${start_date}&end_date=${end_date}`);
      setModulesSummary(response);
    } catch (error) {
      console.error('Erro ao buscar resumo:', error);
    }
  }, [getDateRange]);

  const fetchCashFlow = useCallback(async () => {
    try {
      const { start_date, end_date } = getDateRange();
      const response = await fetchAPI(`${API_URL}/api/reports/modules/cash-flow?start_date=${start_date}&end_date=${end_date}`);
      setCashFlow(response);
    } catch (error) {
      console.error('Erro ao buscar fluxo de caixa:', error);
    }
  }, [getDateRange]);

  const fetchModuleProducts = useCallback(async () => {
    try {
      const { start_date, end_date } = getDateRange();
      const response = await fetchAPI(`${API_URL}/api/reports/modules/${selectedModule}/products?start_date=${start_date}&end_date=${end_date}`);
      setModuleProducts(response.produtos || []);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    }
  }, [getDateRange, selectedModule]);

  const fetchModuleSales = useCallback(async () => {
    try {
      const { start_date, end_date } = getDateRange();
      const response = await fetchAPI(`${API_URL}/api/reports/modules/${selectedModule}/sales?start_date=${start_date}&end_date=${end_date}`);
      setModuleSales(response);
    } catch (error) {
      console.error('Erro ao buscar vendas:', error);
    }
  }, [getDateRange, selectedModule]);

  const fetchEnabledModules = useCallback(async () => {
    try {
      const response = await fetchAPI(`${API_URL}/api/tenant/modules`);
      setEnabledModules(response.modules || { pdv: true, whatsapp: false, online: false });
    } catch (error) {
      console.error('Erro ao buscar módulos:', error);
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchModulesSummary(),
      fetchCashFlow(),
      fetchModuleProducts(),
      fetchModuleSales(),
      fetchEnabledModules()
    ]);
    setLoading(false);
  }, [fetchModulesSummary, fetchCashFlow, fetchModuleProducts, fetchModuleSales, fetchEnabledModules]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    fetchModuleProducts();
    fetchModuleSales();
  }, [selectedModule, fetchModuleProducts, fetchModuleSales]);

  const formatCurrency = (value) => {
    // Handle string values like "R$4.50"
    if (typeof value === 'string') {
      const cleaned = value.replace(/[R$\s]/g, '').replace(',', '.');
      value = parseFloat(cleaned) || 0;
    }
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const getModuleIcon = (module) => {
    switch (module) {
      case 'pdv': return <Store className="w-5 h-5" />;
      case 'whatsapp': return <MessageCircle className="w-5 h-5" />;
      case 'online': case 'catalogo': return <Globe className="w-5 h-5" />;
      default: return <ShoppingCart className="w-5 h-5" />;
    }
  };

  const getModuleColor = (module) => {
    switch (module) {
      case 'pdv': return 'bg-blue-500';
      case 'whatsapp': return 'bg-green-500';
      case 'online': case 'catalogo': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Relatórios por Módulo</h1>
          <p className="text-slate-500">Análise de vendas separada por canal</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40" data-testid="date-range-select">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 dias</SelectItem>
              <SelectItem value="15">Últimos 15 dias</SelectItem>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="60">Últimos 60 dias</SelectItem>
              <SelectItem value="90">Últimos 90 dias</SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={fetchAllData} variant="outline" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Cards de Resumo por Módulo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Geral */}
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white" data-testid="total-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-300 text-sm">Total Geral</p>
                <p className="text-2xl font-bold mt-1">
                  {formatCurrency(modulesSummary?.totais?.total_valor)}
                </p>
                <p className="text-slate-400 text-xs mt-1">
                  {modulesSummary?.totais?.total_vendas || 0} vendas
                </p>
              </div>
              <div className="bg-white/10 p-3 rounded-lg">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cards por Módulo */}
        {modulesSummary?.modulos?.map((modulo) => (
          <Card key={modulo.modulo} className="hover:shadow-lg transition-shadow" data-testid={`module-card-${modulo.modulo}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">{modulo.modulo_nome}</p>
                  <p className="text-xl font-bold mt-1">{formatCurrency(modulo.total_valor)}</p>
                  <p className="text-slate-400 text-xs mt-1">
                    {modulo.total_vendas} vendas • TM: {formatCurrency(modulo.ticket_medio)}
                  </p>
                </div>
                <div className={`${getModuleColor(modulo.modulo)} p-3 rounded-lg text-white`}>
                  {getModuleIcon(modulo.modulo)}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs de Relatórios */}
      <Tabs defaultValue="cashflow" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="cashflow" data-testid="tab-cashflow">
            <DollarSign className="w-4 h-4 mr-2" />
            Fluxo de Caixa
          </TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">
            <Package className="w-4 h-4 mr-2" />
            Produtos Vendidos
          </TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Vendas Detalhadas
          </TabsTrigger>
        </TabsList>

        {/* Fluxo de Caixa por Módulo */}
        <TabsContent value="cashflow" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {cashFlow?.modulos?.map((modulo) => (
              <Card key={modulo.modulo} data-testid={`cashflow-${modulo.modulo}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <div className={`${getModuleColor(modulo.modulo)} p-2 rounded-lg text-white`}>
                      {getModuleIcon(modulo.modulo)}
                    </div>
                    {modulo.modulo_nome}
                    <span className="ml-auto font-bold text-green-600">
                      {formatCurrency(modulo.total_modulo)}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {modulo.pagamentos?.map((pag, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                        <span className="text-slate-600">{pag.forma_nome || pag.forma}</span>
                        <div className="text-right">
                          <span className="font-medium">{formatCurrency(pag.total)}</span>
                          <span className="text-slate-400 text-xs ml-2">({pag.quantidade}x)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Total Geral */}
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-6">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium text-green-800">Total Geral (Todos os Módulos)</span>
                <span className="text-2xl font-bold text-green-600">{formatCurrency(cashFlow?.total_geral)}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Produtos Vendidos */}
        <TabsContent value="products" className="space-y-4">
          <div className="flex items-center gap-4 mb-4">
            <Select value={selectedModule} onValueChange={setSelectedModule}>
              <SelectTrigger className="w-48" data-testid="module-select">
                <SelectValue placeholder="Selecione o módulo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Módulos</SelectItem>
                <SelectItem value="pdv">PDV</SelectItem>
                {enabledModules.whatsapp && <SelectItem value="whatsapp">WhatsApp</SelectItem>}
                {enabledModules.online && <SelectItem value="online">Online</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Produtos Mais Vendidos
                {selectedModule !== 'all' && (
                  <span className="text-sm font-normal text-slate-500">
                    - {selectedModule.toUpperCase()}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-slate-600 font-medium">#</th>
                      <th className="text-left py-3 px-4 text-slate-600 font-medium">Produto</th>
                      <th className="text-right py-3 px-4 text-slate-600 font-medium">Qtd. Vendida</th>
                      <th className="text-right py-3 px-4 text-slate-600 font-medium">Valor Total</th>
                      <th className="text-right py-3 px-4 text-slate-600 font-medium">Vezes Vendido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {moduleProducts.map((produto, index) => (
                      <tr key={produto.product_id || index} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-slate-500">{index + 1}</td>
                        <td className="py-3 px-4 font-medium">{produto.nome}</td>
                        <td className="py-3 px-4 text-right">{produto.quantidade_total}</td>
                        <td className="py-3 px-4 text-right text-green-600 font-medium">
                          {formatCurrency(produto.valor_total)}
                        </td>
                        <td className="py-3 px-4 text-right text-slate-500">{produto.vezes_vendido}x</td>
                      </tr>
                    ))}
                    {moduleProducts.length === 0 && (
                      <tr>
                        <td colSpan="5" className="py-8 text-center text-slate-500">
                          Nenhum produto vendido no período selecionado
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendas Detalhadas */}
        <TabsContent value="sales" className="space-y-4">
          <div className="flex items-center gap-4 mb-4">
            <Select value={selectedModule} onValueChange={setSelectedModule}>
              <SelectTrigger className="w-48" data-testid="sales-module-select">
                <SelectValue placeholder="Selecione o módulo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Módulos</SelectItem>
                <SelectItem value="pdv">PDV</SelectItem>
                {enabledModules.whatsapp && <SelectItem value="whatsapp">WhatsApp</SelectItem>}
                {enabledModules.online && <SelectItem value="online">Online</SelectItem>}
              </SelectContent>
            </Select>
            
            <span className="text-slate-500">
              {moduleSales.total} vendas encontradas
            </span>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-3 px-4 text-slate-600 font-medium">Data/Hora</th>
                      <th className="text-left py-3 px-4 text-slate-600 font-medium">Módulo</th>
                      <th className="text-left py-3 px-4 text-slate-600 font-medium">Cliente</th>
                      <th className="text-center py-3 px-4 text-slate-600 font-medium">Itens</th>
                      <th className="text-right py-3 px-4 text-slate-600 font-medium">Total</th>
                      <th className="text-left py-3 px-4 text-slate-600 font-medium">Pagamento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {moduleSales.sales?.map((venda, index) => (
                      <tr key={venda.id || index} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm">
                          {new Date(venda.created_at).toLocaleString('pt-BR')}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white ${getModuleColor(venda.source || 'pdv')}`}>
                            {getModuleIcon(venda.source || 'pdv')}
                            {(venda.source || 'pdv').toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 px-4">{venda.customer_name || '-'}</td>
                        <td className="py-3 px-4 text-center">{venda.items?.length || 0}</td>
                        <td className="py-3 px-4 text-right font-medium text-green-600">
                          {formatCurrency(venda.total)}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {getSalePaymentLabel(venda)}
                        </td>
                      </tr>
                    ))}
                    {moduleSales.sales?.length === 0 && (
                      <tr>
                        <td colSpan="6" className="py-8 text-center text-slate-500">
                          Nenhuma venda encontrada no período selecionado
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
